// ─── Neural Dock Navbar — OpticVance ──────────────────────────
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */
const DOCK_H_LG = 100;
const DOCK_H_SM = 70;
const ARC_PEAK_LG = 42;
const ARC_PEAK_SM = 24;
const SCROLL_T = 8;    // scroll-direction delta threshold
const HUB_IDX  = 3;    // hub sits in the middle of 7 items
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 640;
const getDockH = () => isMobile() ? DOCK_H_SM : DOCK_H_LG;
const getArcPeak = () => isMobile() ? ARC_PEAK_SM : ARC_PEAK_LG;

/* ═══════════════════════════════════════════════
   ICON SVGs — enlarged, active-aware
   ═══════════════════════════════════════════════ */

/* Brain — Home */
const BrainIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C9.5 2 7.5 3.8 7.1 6.2A3.5 3.5 0 004 9.5c0 1.5.9 2.7 2.2 3.3A4.5 4.5 0 008 18h8a4.5 4.5 0 001.8-5.2A3.5 3.5 0 0020 9.5a3.5 3.5 0 00-3.1-3.3A5 5 0 0012 2z"/>
    <path d="M12 7v5" strokeOpacity=".8"/>
    <circle cx="12" cy="14" r="1.4" fill={active ? '#08CB00' : '#54E346'} stroke="none"/>
  </svg>
);

/* Gears — Features */
const GearsIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.6" strokeLinecap="round">
    <circle cx="9.5" cy="9.5" r="3.2"/>
    <path d="M9.5 5.5v-1M9.5 14.5v1M6 7l-.7-.7M13 12l.7.7M5.5 9.5h-1M14.5 9.5h1M6 12l-.7.7M13 7l.7-.7"/>
    <circle cx="17" cy="17" r="2.2"/>
    <path d="M17 14.5v-.6M17 19.5v.6M15.2 15.2l-.4-.4M19.2 18.8l.4.4M14.5 17h-.6M19.5 17h.6M15.2 18.8l-.4.4M19.2 15.2l.4-.4"/>
  </svg>
);

/* Circuit — How It Works */
const CircuitIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.6" strokeLinecap="round">
    <rect x="2" y="10" width="4" height="4" rx="1"/>
    <rect x="18" y="10" width="4" height="4" rx="1"/>
    <rect x="10" y="2" width="4" height="4" rx="1"/>
    <rect x="10" y="18" width="4" height="4" rx="1"/>
    <path d="M6 12h4M14 12h4M12 6v4M12 14v4"/>
    <circle cx="12" cy="12" r="2" fill={active ? '#08CB00' : 'none'}
      stroke={active ? '#08CB00' : '#54E346'} strokeWidth="1.5"/>
  </svg>
);

/* Hub — Central brain-node logo (larger) */
const HubLogo = () => (
  <svg viewBox="0 0 36 36" className="w-10 h-10" fill="none">
    <circle cx="18" cy="18" r="3.5" fill="#ffffff"/>
    <circle cx="18" cy="4"  r="3" stroke="#ffffff" strokeWidth="1.8" fill="rgba(255,255,255,0.15)"/>
    <circle cx="5"  cy="28" r="3" stroke="#ffffff" strokeWidth="1.8" fill="rgba(255,255,255,0.15)"/>
    <circle cx="31" cy="28" r="3" stroke="#ffffff" strokeWidth="1.8" fill="rgba(255,255,255,0.15)"/>
    <circle cx="3"  cy="14" r="2.2" stroke="#ffffff" strokeWidth="1.4" strokeOpacity=".7"/>
    <circle cx="33" cy="14" r="2.2" stroke="#ffffff" strokeWidth="1.4" strokeOpacity=".7"/>
    <line x1="18" y1="7.2"  x2="18" y2="14.5" stroke="#ffffff" strokeWidth="1.5"/>
    <line x1="7.5" y1="26"  x2="14.5" y2="20" stroke="#ffffff" strokeWidth="1.5"/>
    <line x1="28.5" y1="26" x2="21.5" y2="20" stroke="#ffffff" strokeWidth="1.5"/>
    <line x1="5.5"  y1="15" x2="14"   y2="17" stroke="#ffffff" strokeWidth="1.2" strokeOpacity=".6"/>
    <line x1="30.5" y1="15" x2="22"   y2="17" stroke="#ffffff" strokeWidth="1.2" strokeOpacity=".6"/>
  </svg>
);

/* Diamond — Pricing */
const PricingIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h12l4 7-10 12L2 10z"/>
    <path d="M2 10h20" strokeOpacity=".55"/>
    <path d="M12 3l-2 7 2 12 2-12-2-7" strokeOpacity=".45"/>
  </svg>
);

/* Person — About */
const AboutIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="7" r="4"/>
    <path d="M4 21v-1a7 7 0 017-7h2a7 7 0 017 7v1"/>
  </svg>
);

/* Signal — Contact */
const ContactIcon = ({ active }) => (
  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none"
    stroke={active ? '#ffffff' : '#54E346'} strokeWidth="1.7" strokeLinecap="round">
    <circle cx="12" cy="14" r="2.2"/>
    <path d="M8 10.5a5.5 5.5 0 018 0"/>
    <path d="M5 7.5a9.5 9.5 0 0114 0"/>
    <path d="M12 16.2V20"/>
    <circle cx="12" cy="21.5" r="1.3" fill={active ? '#08CB00' : '#54E346'} stroke="none"/>
  </svg>
);

/* ═══════════════════════════════════════════════
   NAV ITEMS — order defines arc position
   ═══════════════════════════════════════════════ */
const NAV_ITEMS = [
  { id: 'home',    label: 'Home',      href: '#hero',         Icon: BrainIcon   },
  { id: 'about',   label: 'About',     href: '#about',        Icon: AboutIcon    },
  { id: 'feat',    label: 'Features',  href: '#features',     Icon: GearsIcon   },
  { id: 'hub',     label: 'Opticore',  href: null,            Icon: HubLogo, isHub: true },
  { id: 'how',     label: 'How Works', href: '#how-it-works', Icon: CircuitIcon  },
  { id: 'price',   label: 'Pricing',   href: '#pricing',      Icon: PricingIcon  },
  { id: 'contact', label: 'Contact',   href: '#contact',      Icon: ContactIcon  },
];

const SECTIONS = NAV_ITEMS.filter((it) => it.href);

/* ═══════════════════════════════════════════════
   ARC HELPER — parabolic Y offset
   ═══════════════════════════════════════════════ */
const arcY = (i) => {
  const n = (i - HUB_IDX) / HUB_IDX;        // -1 → +1
  return -getArcPeak() * (1 - n * n);            // 0 at edges, -PEAK at centre
};

/* ═══════════════════════════════════════════════
   ANIMATED SCAN LINE
   ═══════════════════════════════════════════════ */
const ScanLine = () => (
  <>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="absolute top-0 h-[3px] w-32 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, #54E346 50%, transparent)',
          boxShadow: '0 0 10px 3px rgba(84,227,70,0.6)',
        }}
        animate={{ x: ['-25%', '125%'] }}
        transition={{ duration: 3.6, repeat: Infinity, delay: i * 1.2, ease: 'linear' }}
        aria-hidden="true"
      />
    ))}
  </>
);

/* ═══════════════════════════════════════════════
   ROTATING GRADIENT HUB RING
   ═══════════════════════════════════════════════ */
const RotatingRing = ({ size }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size + 16,
      height: size + 16,
      top: -8, left: -8,
      background: 'conic-gradient(from 0deg, #08CB00 0%, #54E346 30%, rgba(255,255,255,0.6) 50%, #54E346 70%, #08CB00 100%)',
      filter: 'blur(3px)',
    }}
    animate={{ rotate: 360 }}
    transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
    aria-hidden="true"
  />
);

/* ═══════════════════════════════════════════════
   BRANCH LINES — curved SVG from hub to each icon
   ═══════════════════════════════════════════════ */
const BranchLines = ({ positions, activeIdx }) => {
  const hub = positions[HUB_IDX];
  if (!hub) return null;
  return (
    <svg className="absolute inset-0 pointer-events-none z-0" style={{ overflow: 'visible' }} aria-hidden="true">
      <defs>
        <filter id="bl-glow">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="bl-glow-sm">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {positions.map((pos, i) => {
        if (i === HUB_IDX || !pos) return null;
        const active = i === activeIdx;
        const mx = (hub.x + pos.x) / 2;
        const my = Math.min(hub.y, pos.y) - 22;
        return (
          <motion.path
            key={NAV_ITEMS[i].id}
            d={`M${hub.x},${hub.y} Q${mx},${my} ${pos.x},${pos.y}`}
            fill="none"
            stroke={active ? '#54E346' : '#08CB00'}
            strokeWidth={active ? 3 : 1.2}
            filter={active ? 'url(#bl-glow)' : 'url(#bl-glow-sm)'}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: active ? 1 : 0.2 }}
            transition={{
              pathLength: { duration: 0.9, delay: 0.3 + Math.abs(i - HUB_IDX) * 0.07, ease: 'easeOut' },
              opacity: { duration: 0.2 },
            }}
          />
        );
      })}
    </svg>
  );
};

/* ═══════════════════════════════════════════════
   MAIN NAVBAR — neural docking station
   ═══════════════════════════════════════════════ */
const Navbar = ({ onDemo }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('#hero');
  const [hoveredIdx,    setHoveredIdx]    = useState(null);
  const [dockVisible,   setDockVisible]   = useState(true);
  const [nodePos,       setNodePos]       = useState([]);
  const [menuOpen,      setMenuOpen]      = useState(false);

  const dockRef  = useRef(null);
  const nodeRefs = useRef([]);
  const lastY    = useRef(0);

  const measure = useCallback(() => {
    const dr = dockRef.current?.getBoundingClientRect();
    if (!dr) return;
    setNodePos(
      nodeRefs.current.map((el) => {
        const r = el?.getBoundingClientRect();
        if (!r) return null;
        return { x: r.left - dr.left + r.width / 2, y: r.top - dr.top + r.height / 2 };
      }),
    );
  }, []);

  useEffect(() => {
    const t = setTimeout(measure, 900);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [measure]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const sects = SECTIONS.map((s) => {
        const el = document.querySelector(s.href);
        return el ? { href: s.href, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);
      const cur = sects.reduce((best, s) => (s.top <= 140 ? s : best), sects[0]);
      if (cur) setActiveSection(cur.href);
      const delta = y - lastY.current;
      if (delta < -SCROLL_T) setDockVisible(false);
      else if (delta > SCROLL_T) setDockVisible(true);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = useCallback((href) => {
    const el = document.querySelector(href);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const activeIdx = NAV_ITEMS.findIndex((it) => it.href === activeSection);

  return (
    <>
    {/* ── Desktop / tablet dock — hidden on mobile ── */}
    <motion.div
      className="fixed bottom-0 left-0 right-0 z-50 hidden sm:flex justify-center pointer-events-none"
      animate={{ y: dockVisible ? 0 : getDockH() + 60 }}
      transition={{ type: 'spring', stiffness: 320, damping: 26 }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* ── Multi-layer upward glow aura ── */}
      <div className="absolute inset-x-0 bottom-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-28 left-[2%] right-[2%] h-32"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(8,203,0,0.16) 0%, transparent 60%)', filter: 'blur(28px)' }}
        />
        <div className="absolute -top-16 left-[12%] right-[12%] h-18"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(84,227,70,0.26) 0%, transparent 65%)', filter: 'blur(16px)' }}
        />
        <div className="absolute -top-5 left-[28%] right-[28%] h-6"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(84,227,70,0.5) 0%, transparent 80%)', filter: 'blur(8px)' }}
        />
      </div>

      {/* ── Dock container ── */}
      <div
        ref={dockRef}
        className="relative w-[96%] sm:w-[90%] lg:w-4/5 pointer-events-auto"
        style={{ height: getDockH() + getArcPeak() + 10 }}
      >
        {/* Glassmorphic dock background */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: getDockH() + 14,
            background: 'linear-gradient(180deg, rgba(8,203,0,0.08) 0%, rgba(4,20,4,0.96) 25%, rgba(0,0,0,0.99) 100%)',
            clipPath: 'ellipse(62% 100% at 50% 100%)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
          aria-hidden="true"
        />

        {/* Scan line container at dock top edge */}
        <div
          className="absolute pointer-events-none overflow-hidden"
          style={{ bottom: getDockH() + 12, left: '18%', right: '18%', height: 4 }}
          aria-hidden="true"
        >
          <ScanLine />
        </div>

        {/* Top-edge glow line */}
        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{ height: getDockH() + 14, clipPath: 'ellipse(62% 100% at 50% 100%)' }}
          aria-hidden="true"
        >
          <div
            className="absolute top-0 left-[7%] right-[7%]"
            style={{
              height: 3,
              background: 'linear-gradient(90deg, transparent 0%, #54E346 35%, #ffffff 50%, #54E346 65%, transparent 100%)',
              boxShadow: '0 0 24px 6px rgba(84,227,70,0.45), 0 0 50px 12px rgba(8,203,0,0.15)',
            }}
          />
        </div>

        {/* Branch SVG lines */}
        <BranchLines positions={nodePos} activeIdx={activeIdx} />

        {/* ── Icon node strip ── */}
        <div
          className="relative z-10 flex items-end justify-center h-full"
          style={{ gap: 'clamp(8px, 3vw, 30px)', paddingBottom: 14 }}
          role="menubar"
        >
          {NAV_ITEMS.map((item, i) => {
            const isHub    = item.isHub;
            const isActive = activeIdx === i;
            const isHov    = hoveredIdx === i;
            const mob = isMobile();
            const nodeSize = isHub ? (mob ? 60 : 92) : (mob ? 42 : 66);
            const entryDelay = 0.1 + Math.abs(i - HUB_IDX) * 0.07;

            return (
              <div key={item.id} style={{ transform: `translateY(${arcY(i)}px)` }}>
                <motion.div
                  className="relative flex flex-col items-center gap-2"
                  initial={{ y: 80, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 360, damping: 22, delay: entryDelay }}
                >
                  {/* ── Node ── */}
                  <div className="relative">
                    {/* Rotating ring on hub */}
                    {isHub && <RotatingRing size={nodeSize} />}

                    {/* Active outer breathing ring */}
                    {isActive && !isHub && (
                      <motion.div
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: nodeSize + 18, height: nodeSize + 18,
                          top: -9, left: -9,
                          border: '2px solid #54E346',
                          boxShadow: '0 0 22px 7px rgba(84,227,70,0.45), inset 0 0 10px rgba(84,227,70,0.12)',
                        }}
                        animate={{ scale: [1, 1.07, 1], opacity: [1, 0.55, 1] }}
                        transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
                        aria-hidden="true"
                      />
                    )}

                    <motion.button
                      ref={(el) => { nodeRefs.current[i] = el; }}
                      onClick={() => (isHub ? navigate('/product') : scrollTo(item.href))}
                      onMouseEnter={() => setHoveredIdx(i)}
                      onMouseLeave={() => setHoveredIdx(null)}
                      onFocus={() => setHoveredIdx(i)}
                      onBlur={() => setHoveredIdx(null)}
                      className="relative rounded-full flex items-center justify-center
                                 focus:outline-none focus-visible:ring-4
                                 focus-visible:ring-[#54E346] focus-visible:ring-offset-2
                                 focus-visible:ring-offset-black overflow-hidden"
                      style={{
                        width: nodeSize,
                        height: nodeSize,
                        background: isHub
                          ? 'linear-gradient(135deg, #08CB00 0%, #54E346 50%, #08CB00 100%)'
                          : isActive
                            ? 'radial-gradient(circle at 40% 38%, rgba(84,227,70,0.35) 0%, rgba(8,203,0,0.2) 40%, rgba(0,0,0,0.88) 100%)'
                            : isHov
                              ? 'radial-gradient(circle, rgba(8,203,0,0.18) 0%, rgba(0,0,0,0.84) 100%)'
                              : 'rgba(4,8,4,0.85)',
                        border: isHub
                          ? 'none'
                          : isActive
                            ? '2px solid #54E346'
                            : `1.5px solid ${isHov ? 'rgba(84,227,70,0.65)' : 'rgba(8,203,0,0.38)'}`,
                        boxShadow: isHub
                          ? '0 0 60px rgba(84,227,70,0.55), 0 0 120px rgba(8,203,0,0.22), inset 0 0 24px rgba(255,255,255,0.1)'
                          : isActive
                            ? '0 0 32px rgba(84,227,70,0.6), 0 0 64px rgba(8,203,0,0.22), inset 0 0 16px rgba(84,227,70,0.14)'
                            : isHov
                              ? '0 0 20px rgba(8,203,0,0.4), inset 0 0 10px rgba(84,227,70,0.07)'
                              : '0 4px 16px rgba(0,0,0,0.7)',
                      }}
                      whileHover={{ scale: isHub ? 1.1 : 1.16, y: -5 }}
                      whileTap={{ scale: 0.87 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                      role="menuitem"
                      aria-label={isHub ? 'OpticVance — Opticore' : item.label}
                      aria-current={isActive && !isHub ? 'page' : undefined}
                    >
                      {/* Hub shimmer sweep */}
                      {isHub && (
                        <motion.div
                          className="absolute inset-0 rounded-full pointer-events-none"
                          style={{ background: 'linear-gradient(105deg, transparent 25%, rgba(255,255,255,0.35) 50%, transparent 75%)' }}
                          animate={{ x: ['-80%', '80%'] }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'linear', repeatDelay: 1.2 }}
                          aria-hidden="true"
                        />
                      )}

                      {/* Idle pulse ring */}
                      <motion.span
                        className="absolute inset-0 rounded-full pointer-events-none"
                        style={{ border: `1.5px solid ${isHub ? 'rgba(255,255,255,0.55)' : isActive ? 'rgba(84,227,70,0.65)' : 'rgba(8,203,0,0.28)'}` }}
                        animate={{ scale: [1, isHub ? 1.6 : isActive ? 1.5 : 1.38], opacity: [0.65, 0] }}
                        transition={{ duration: isHub ? 1.8 : 2.4, repeat: Infinity, ease: 'easeOut' }}
                        aria-hidden="true"
                      />

                      {/* Second pulse — hub only */}
                      {isHub && (
                        <motion.span
                          className="absolute inset-0 rounded-full border border-white/25 pointer-events-none"
                          animate={{ scale: [1, 2.1], opacity: [0.4, 0] }}
                          transition={{ duration: 2.1, repeat: Infinity, ease: 'easeOut', delay: 0.8 }}
                          aria-hidden="true"
                        />
                      )}

                      {/* Hover ripple */}
                      <AnimatePresence>
                        {isHov && (
                          <motion.span
                            className="absolute inset-0 rounded-full pointer-events-none"
                            style={{ background: 'rgba(8,203,0,0.22)' }}
                            initial={{ scale: 0.5, opacity: 0.8 }}
                            animate={{ scale: 2.8, opacity: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.58, ease: 'easeOut' }}
                            aria-hidden="true"
                          />
                        )}
                      </AnimatePresence>

                      {/* Icon */}
                      <span className="relative z-10">
                        <item.Icon active={isActive || isHub} />
                      </span>
                    </motion.button>
                  </div>

                  {/* ── Persistent label ── */}
                  <motion.span
                    className="text-center leading-none select-none pointer-events-none
                               font-semibold tracking-wide"
                    style={{
                      fontSize: mob ? (isHub ? 9 : 8) : (isHub ? 11 : 10),
                      letterSpacing: isHub ? '0.08em' : '0.04em',
                      color: isHub
                        ? '#ffffff'
                        : isActive
                          ? '#54E346'
                          : isHov
                            ? 'rgba(84,227,70,0.85)'
                            : 'rgba(255,255,255,0.42)',
                      textShadow: (isActive || isHub)
                        ? `0 0 14px ${isHub ? 'rgba(255,255,255,0.6)' : 'rgba(84,227,70,0.8)'}`
                        : 'none',
                    }}
                    animate={{
                      opacity: isHub ? 1 : isActive ? 1 : isHov ? 0.9 : 0.42,
                    }}
                    transition={{ duration: 0.18 }}
                  >
                    {item.label}
                  </motion.span>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>

    {/* ── Mobile hamburger button (sm and below) ── */}
    <div className="sm:hidden fixed top-4 right-4 z-[60] pointer-events-auto">
      <motion.button
        onClick={() => setMenuOpen((o) => !o)}
        className="w-11 h-11 rounded-xl flex items-center justify-center"
        style={{
          background: 'rgba(4, 20, 4, 0.92)',
          border: '1.5px solid rgba(84, 227, 70, 0.45)',
          boxShadow: '0 0 18px rgba(8, 203, 0, 0.3)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
        whileTap={{ scale: 0.88 }}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
      >
        <AnimatePresence mode="wait" initial={false}>
          {menuOpen ? (
            <motion.svg key="x" viewBox="0 0 24 24" fill="none" stroke="#54E346" strokeWidth="2.2"
              strokeLinecap="round" className="w-5 h-5"
              initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
              <path d="M18 6 6 18M6 6l12 12" />
            </motion.svg>
          ) : (
            <motion.svg key="ham" viewBox="0 0 24 24" fill="none" stroke="#54E346" strokeWidth="2.2"
              strokeLinecap="round" className="w-5 h-5"
              initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
              <path d="M4 6h16M4 12h16M4 18h16" />
            </motion.svg>
          )}
        </AnimatePresence>
      </motion.button>
    </div>

    {/* ── Mobile slide-in drawer ── */}
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          className="sm:hidden fixed inset-0 z-[55] pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/75"
            style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
            onClick={() => setMenuOpen(false)}
          />

          {/* Drawer panel */}
          <motion.nav
            className="absolute top-0 right-0 bottom-0 w-72 flex flex-col"
            style={{
              background: 'linear-gradient(160deg, rgba(4,20,4,0.98) 0%, rgba(0,0,0,0.99) 100%)',
              borderLeft: '1px solid rgba(84,227,70,0.2)',
              boxShadow: '-20px 0 60px rgba(8,203,0,0.08)',
            }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 32 }}
            aria-label="Mobile navigation"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-primary/10">
              <div className="flex items-center gap-3">
                <img src="/logo.svg" alt="OpticVance" className="w-9 h-9"
                  style={{ filter: 'drop-shadow(0 0 8px rgba(8,203,0,0.4))' }} />
                <span className="font-bold text-base">
                  <span className="text-primary">Optic</span>
                  <span className="text-white">Vance</span>
                </span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                aria-label="Close menu"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 overflow-y-auto px-4 py-5 space-y-1">
              {NAV_ITEMS.map((item, i) => {
                const isNavActive = activeSection === item.href;
                const isHubItem   = item.isHub;
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => { setMenuOpen(false); if (isHubItem) navigate('/product'); else scrollTo(item.href); }}
                    className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl text-left transition-all duration-200"
                    style={{
                      background: isHubItem
                        ? 'linear-gradient(135deg, rgba(8,203,0,0.2) 0%, rgba(84,227,70,0.1) 100%)'
                        : isNavActive ? 'rgba(84,227,70,0.08)' : 'transparent',
                      border: isHubItem
                        ? '1px solid rgba(84,227,70,0.4)'
                        : isNavActive ? '1px solid rgba(84,227,70,0.25)' : '1px solid transparent',
                    }}
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.04 + 0.1, type: 'spring', stiffness: 300, damping: 24 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: isHubItem ? 'linear-gradient(135deg, #08CB00, #54E346)' : 'rgba(8,203,0,0.1)' }}
                    >
                      <item.Icon active={isNavActive || isHubItem} />
                    </span>
                    <span className={`text-sm font-semibold ${isHubItem ? 'text-white' : isNavActive ? 'text-primary' : 'text-gray-300'}`}>
                      {item.label}
                    </span>
                    {isHubItem && (
                      <span className="ml-auto text-[10px] font-mono text-primary/60 border border-primary/20 px-1.5 py-0.5 rounded-full">
                        Open
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 pt-4 border-t border-primary/10">
              <p className="text-[10px] text-gray-600 font-mono text-center">© 2026 OpticVance Inc.</p>
            </div>
          </motion.nav>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Navbar;
